import { useEffect, useRef, useState } from "react";
import Game from "@/game/engine";
import { CHARACTERS } from "@/game/config";
import { preloadAssets } from "@/game/assets";
import { AudioManager } from "@/game/audio";
import { Hud } from "@/components/Hud";
import SettingsMenu from "@/components/SettingsMenu";
import { MusicPicker } from "@/components/MusicPicker";
import { useLang } from "@/i18n";
import { useMusic, SONGS } from "@/music";
import { api } from "@/lib/api";
import { shareNicaBros } from "@/lib/share";
import { stopTitleAudio } from "@/lib/audioBus";
import { toast } from "sonner";
import { useAuth } from "@/auth";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;

// "Restaurante El Timón — San Juan del Sur" / "Pan de Vida Granada" (no duplicate city).
const sponsorCaption = (l) => { const n = l.sponsorFullName || l.sponsorName || ""; return l.city && !n.includes(l.city) ? `${n} — ${l.city}` : n; };

export default function GameCanvas({ character, level, difficulty, onWin, onGameOver, onExit, onRestart, onSuggest }) {
  const { lang, t } = useLang();
  const { songId } = useMusic();
  const { user, refreshUser } = useAuth();
  const bonusLives = (user && (user.invites >= 1 || user.referred_by)) ? 1 : 0;
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  const songRef = useRef(null);
  const adVideosRef = useRef([]);
  const chestWeekendRef = useRef(false);
  const [chestReward, setChestReward] = useState(null); // {coins,char,weekend} | {error}
  const [chestClaiming, setChestClaiming] = useState(false);
  const [hud, setHud] = useState({ score: 0, lives: 3, beers: 0, fiesta: 0, powered: false, levelName: level.name, short: level.short, boss: null });
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [musicVol, setMusicVol] = useState(() => { const v = parseFloat(localStorage.getItem("nicabros_music_vol")); return isNaN(v) ? 0.55 : v; });
  const [sfxVol, setSfxVol] = useState(() => { const v = parseFloat(localStorage.getItem("nicabros_sfx_vol")); return isNaN(v) ? 0.6 : v; });
  const [loading, setLoading] = useState(true);
  const [loadPct, setLoadPct] = useState(0);
  const [showMusic, setShowMusic] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(false);

  // Each level ALWAYS plays its OWN assigned song (config `level.song`) by default,
  // regardless of whatever track is selected on the title-screen music player.
  // Only an explicit in-game track switch (while playing) overrides the level song.
  const [autoOtherId] = useState(() => {
    const pool = SONGS.filter((s) => !s.noFile && s.id !== "nicaragua");
    return pool[Math.floor(Math.random() * pool.length)].id;
  });
  const initialSongId = useRef(songId);
  const levelDefaultId = level.song || (level.theme === "cerronegro" ? "nicaragua" : autoOtherId);
  // songId differs from the value at game start => the player picked a track in-game.
  const manualInGamePick = songId !== initialSongId.current && songId !== "auto";
  const resolvedId = manualInGamePick ? songId : levelDefaultId;
  const song = SONGS.find((s) => s.id === resolvedId) || SONGS[0];
  const songSrc = `${base}/${song.file}`;

  // Hard-stop the landing-page music the instant gameplay mounts — guarantees it
  // never bleeds over the level soundtrack, even on iOS where a detached <audio>
  // element keeps playing after React removes it from the DOM.
  useEffect(() => { stopTitleAudio(); }, []);

  // Show a brief "Now playing" toast right when the level starts.
  useEffect(() => {
    if (!song.spotifyUrl) return;
    setNowPlaying(true);
    const tmo = setTimeout(() => setNowPlaying(false), 3500);
    return () => clearTimeout(tmo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let game;
    let cancelled = false;
    const songEl = songRef.current;
    const loadSponsorAd = async () => {
      try {
        const { data } = await api.sponsorAds();
        const BK = process.env.REACT_APP_BACKEND_URL;
        const forLevel = (data || []).filter((x) => x.level_index === level.index && x.enabled);
        const loadImg = (url) => new Promise((res) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = () => res(null);
          im.src = url;
        });
        const makeVideo = (url) => {
          const v = document.createElement("video");
          v.muted = true; v.loop = true; v.playsInline = true; v.preload = "auto";
          v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", "");
          v.crossOrigin = "anonymous";
          // Keep the video technically on-screen (1px, near-invisible) — iOS/WebKit
          // refuses to decode frames for canvas drawing when a video is fully off-screen.
          v.style.cssText = "position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0.01;pointer-events:none;z-index:0;";
          v.src = url;
          document.body.appendChild(v);
          adVideosRef.current.push(v);
          v.load();
          v.play().then(() => v.pause()).catch(() => {});  // prime the decoder
          return v;
        };
        const loadOne = async (ad) => {
          if (!ad || (!ad.image_url && !ad.video_url)) return null;
          const img = ad.image_url ? await loadImg(`${BK}${ad.image_url}`) : null;
          const video = ad.video_url ? makeVideo(`${BK}${ad.video_url}`) : null;
          if (!img && !video) return null;
          return {
            img, video, link: ad.link,
            captionEn: ad.caption_en || "", captionEs: ad.caption_es || "",
            transform: ad.transform || { zoom: 1, offset_x: 0, offset_y: 0, fit: "cover" },
          };
        };
        const [start, end] = await Promise.all([
          loadOne(forLevel.find((x) => x.slot === "start")),
          loadOne(forLevel.find((x) => x.slot === "end")),
        ]);
        let s = start, e = end;
        // Featured partner level: preset El Timón as the default billboard when an
        // admin hasn't configured a sponsor ad for a slot (admin uploads override this).
        if (level.featured && (level.sponsorLogo || level.sponsorLink)) {
          const logo = level.sponsorLogo ? await loadImg(`${process.env.PUBLIC_URL || ""}/game-assets/${level.sponsorLogo}.webp`) : null;
          const def = (logo || level.sponsorLink) ? {
            img: logo, video: null, link: level.sponsorLink || "",
            captionEn: sponsorCaption(level),
            captionEs: sponsorCaption(level),
            transform: { zoom: 1, offset_x: 0, offset_y: 0, fit: "contain" },
          } : null;
          if (!s && def) s = def;
          if (!e && def) e = def;
        }
        return { start: s, end: e };
      } catch { return {}; }
    };
    const levelNeed = [
      "abuela", "beer", "boss", "chancleta", "dog", "pinata", "police",
      level.bg, level.cardBg, level.abuelaSprite, level.hazardSprite, level.itemSprite, ...(level.itemSprites || []),
      character?.spriteId, character?.id,
      ...(character?.variants?.map((v) => v.spriteId || v.id) || []),
      ...(level.bgSegs || []),
    ].filter(Boolean);
    Promise.all([preloadAssets(levelNeed, (l, tot) => { if (!cancelled) setLoadPct(Math.round((l / Math.max(1, tot)) * 100)); })]).then(([assets]) => {
      if (cancelled) return;
      const audio = new AudioManager();
      audioRef.current = audio;
      audio.setVolume(sfxVol);
      // Ads load in the background and are injected once ready — never blocks level start.
      let sponsorAds = {};
      const onAdClick = (slot = "start") => {
        const ad = sponsorAds && sponsorAds[slot];
        if (ad && ad.link) {
          // Pause the game so the player keeps their spot and can't die while away.
          gameRef.current?.pause();
          songRef.current?.pause();
          setPaused(true);
          api.trackSponsorClick(level.index, slot);
          window.open(ad.link, "_blank", "noopener");
        } else if (!ad) {
          // Default "ADS HERE" billboard → share the app with friends.
          const text = lang === "en"
            ? "Play Nica Bros — the Nicaragua arcade game! 🇳🇮🎮"
            : "¡Jugá Nica Bros, el juego pinolero de Nicaragua! 🇳🇮🎮";
          shareNicaBros({ text }).then((r) => {
            if (r === "copied") toast.success(lang === "en" ? "Link copied — share it!" : "¡Link copiado, compartilo!");
          });
        }
      };
      const onChest = () => {
        gameRef.current?.pause();
        songRef.current?.pause();
        setChestClaiming(true);
        setChestReward(null);
        api.treasureClaim(level.index, chestWeekendRef.current)
          .then(({ data }) => { setChestReward(data.reward); refreshUser && refreshUser(); })
          .catch((e) => { setChestReward({ error: e?.response?.data?.detail || "Could not open the chest." }); })
          .finally(() => setChestClaiming(false));
      };
      game = new Game(canvasRef.current, {
        character,
        levelMeta: level,
        assets,
        audio,
        lang,
        diff: difficulty,
        sponsorAds,
        bonusLives,
        callbacks: { onHud: setHud, onWin, onGameOver, onAdClick, onChest },
      });
      gameRef.current = game;
      if (typeof window !== "undefined") window.__nicaGame = game;
      game.start();
      // (Per-level in-game treasure chest removed — the daily check-in chest lives on the main page.)
      // Mobile browsers block video.play() until a user gesture. Prime every ad
      // video on the first touch/click anywhere so frames decode for canvas drawing.
      const unlockVideos = () => {
        adVideosRef.current.forEach((v) => {
          v.play().then(() => { if (!v.dataset.onscreen) v.pause(); }).catch(() => {});
        });
      };
      window.addEventListener("pointerdown", unlockVideos, { once: true });
      window.addEventListener("touchend", unlockVideos, { once: true });
      // Fetch sponsor ads in the background; inject into the running game as soon as ready.
      loadSponsorAd().then((ads) => {
        if (cancelled || !ads) return;
        sponsorAds = ads;
        gameRef.current?.setSponsorAds(ads);
      });
      const s = songRef.current;
      if (s) {
        s.volume = musicVol;
        s.muted = muted;
        s.play().catch(() => {});
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
      game?.destroy();
      audioRef.current?.destroy();
      songEl?.pause();
      adVideosRef.current.forEach((v) => { try { v.pause(); v.removeAttribute("src"); v.load(); v.remove(); } catch (e) {} });
      adVideosRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live song switch: when the selected track changes, load + play the new one.
  useEffect(() => {
    const s = songRef.current;
    if (!s || loading) return;
    s.volume = musicVol;
    s.muted = muted;
    if (!paused) s.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songSrc]);

  // Autoplay policy: when the level is entered via a direct share link there is
  // no prior user gesture, so the browser blocks the level song. Start it on the
  // first input (tap/key/touch) so music always plays.
  useEffect(() => {
    let done = false;
    const tryPlay = () => {
      const s = songRef.current;
      if (done || !s) return;
      s.volume = musicVol;
      s.muted = muted;
      s.play().then(() => { done = true; cleanup(); }).catch(() => {});
    };
    const cleanup = () => {
      ["pointerdown", "keydown", "touchstart"].forEach((e) => window.removeEventListener(e, tryPlay));
    };
    ["pointerdown", "keydown", "touchstart"].forEach((e) => window.addEventListener(e, tryPlay));
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMusicVol = (v) => {
    setMusicVol(v);
    localStorage.setItem("nicabros_music_vol", String(v));
    if (songRef.current) songRef.current.volume = v;
  };
  const changeSfxVol = (v) => {
    setSfxVol(v);
    localStorage.setItem("nicabros_sfx_vol", String(v));
    audioRef.current?.setVolume(v);
    audioRef.current?.sfx("coin");
  };

  const togglePause = () => {
    const g = gameRef.current;
    if (!g) return;
    if (paused) { g.resume(); songRef.current?.play().catch(() => {}); setPaused(false); }
    else { g.pause(); songRef.current?.pause(); setPaused(true); }
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    audioRef.current?.setMuted(m);
    if (songRef.current) songRef.current.muted = m;
  };

  const handleTouch = (name, val) => gameRef.current?.setInput(name, val);

  const closeChest = () => {
    setChestReward(null);
    setChestClaiming(false);
    if (!paused && !showSettings) { gameRef.current?.resume(); songRef.current?.play().catch(() => {}); }
  };
  const giftChar = chestReward?.char ? CHARACTERS.find((c) => c.id === chestReward.char) : null;

  const openSettings = () => {
    gameRef.current?.pause();
    songRef.current?.pause();
    setShowSettings(true);
  };
  const closeSettings = () => {
    setShowSettings(false);
    if (!paused) { gameRef.current?.resume(); songRef.current?.play().catch(() => {}); }
  };

  return (
    <div className="relative w-full h-full bg-black overflow-hidden" style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" }}>
      <div className="relative w-full h-full">
        <canvas id="game-canvas" ref={canvasRef} width={1920} height={1080} />
        <audio ref={songRef} src={songSrc} loop preload="auto" />
        <Hud hud={hud} character={character} charColor={character.colors.accent} muted={muted} onMute={toggleMute} onPause={togglePause} onMusic={() => setShowMusic(true)} onSettings={openSettings} onTouch={handleTouch} itemEmoji={level.itemSprite === "bread" ? "🥖" : "🍺"} />

        {nowPlaying && song.spotifyUrl && (
          <>
            <style>{`@keyframes npSlide{from{opacity:0;transform:translateX(-120%)}to{opacity:1;transform:translateX(0)}}`}</style>
            <div
              data-testid="now-playing-toast"
              className="absolute left-3 bottom-20 z-30 flex items-center gap-2 rounded-lg bg-black/75 backdrop-blur-md ring-1 ring-[#1DB954]/50 px-3 py-1.5 shadow-xl"
              style={{ animation: "npSlide 0.45s cubic-bezier(.2,.8,.2,1)" }}
            >
              <span className="text-[#1DB954] text-lg leading-none">♪</span>
              <div className="leading-tight">
                <p className="font-vt text-[11px] text-white/60">{lang === "en" ? "Now playing" : "Sonando ahora"}</p>
                <p className="font-vt text-sm text-white whitespace-nowrap">{song.title} — <span style={{ color: "#1DB954" }}>{song.artist}</span></p>
              </div>
              <a
                href={song.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="now-playing-follow"
                onClick={() => {
                  gameRef.current?.pause();
                  songRef.current?.pause();
                  setPaused(true);
                  setNowPlaying(false);
                  api.trackSpotifyClick({ song_id: song.id, title: song.title || song.name, artist: song.artist || "", source: "ingame_follow" });
                }}
                className="ml-0.5 shrink-0 rounded-full bg-[#1DB954] text-black font-pixel text-[9px] px-2.5 py-1.5 hover:scale-105 active:scale-95 transition-transform"
              >
                {lang === "en" ? "Follow" : "Seguir"}
              </a>
            </div>
          </>
        )}

        <SettingsMenu
          open={showSettings}
          onClose={closeSettings}
          muted={muted}
          onToggleSound={toggleMute}
          musicVol={musicVol}
          sfxVol={sfxVol}
          onMusicVol={changeMusicVol}
          onSfxVol={changeSfxVol}
          onRestart={onRestart}
          onExit={onExit}
          onSuggest={() => { setShowSettings(false); onSuggest && onSuggest(); }}
        />

        {loading && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-black px-6">
            <p className="font-pixel text-[#FFEB3B] text-sm">{t("loadingGame")}</p>
            <div className="w-64 max-w-[72vw] h-4 rounded-full bg-white/10 ring-1 ring-white/25 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#FFEB3B] to-[#4CAF50] transition-all duration-200 ease-out" style={{ width: `${loadPct}%` }} data-testid="loading-bar" />
            </div>
            <p className="font-vt text-white text-xl tracking-wide" data-testid="loading-pct">{loadPct}%</p>
          </div>
        )}

        {showMusic && (
          <div className="absolute inset-0 z-[55] flex items-center justify-center bg-black/80 scanlines" data-testid="music-overlay">
            <div className="pixel-card bg-[#141414] p-6 text-center pop-in flex flex-col items-center gap-4">
              <h2 className="font-pixel text-sm text-[#FFEB3B] pixel-stroke">{t("chooseSong")}</h2>
              <MusicPicker />
              <button data-testid="music-close-btn" onClick={() => setShowMusic(false)} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs mt-2">
                {t("close")}
              </button>
            </div>
          </div>
        )}

        {paused && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 scanlines" data-testid="pause-overlay">
            <div className="pixel-card bg-[#141414] p-8 text-center pop-in">
              <h2 className="font-pixel text-2xl text-[#FFEB3B] pixel-stroke mb-6">{t("pauseTitle")}</h2>
              <button data-testid="resume-btn" onClick={togglePause} className="pixel-btn bg-[#4CAF50] text-white px-8 py-4 text-sm">
                {t("resume")}
              </button>
            </div>
          </div>
        )}

        {(chestReward || chestClaiming) && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/85 scanlines" data-testid="chest-modal">
            <div className="pixel-card bg-[#141414] p-7 text-center pop-in max-w-sm mx-4">
              {chestClaiming ? (
                <p className="font-pixel text-sm text-[#FFEB3B] blink">{lang === "en" ? "Opening chest…" : "Abriendo cofre…"}</p>
              ) : chestReward?.error ? (
                <>
                  <div className="text-4xl mb-2">🔒</div>
                  <p className="font-vt text-base text-white/80">{lang === "en" ? "Already opened today — come back tomorrow!" : "Ya lo abriste hoy — ¡volvé mañana!"}</p>
                  <button data-testid="chest-close-btn" onClick={closeChest} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs mt-4">{lang === "en" ? "Keep playing" : "Seguir jugando"}</button>
                </>
              ) : (
                <>
                  <h2 className="font-pixel text-sm text-[#FFEB3B] pixel-stroke mb-1">{chestReward?.weekend ? (lang === "en" ? "WEEKEND CHEST!" : "¡COFRE DE FIN DE SEMANA!") : (lang === "en" ? "TREASURE CHEST!" : "¡COFRE DEL TESORO!")}</h2>
                  <div className="text-5xl my-3">🎁</div>
                  <div className="font-vt text-2xl text-[#FFD54F]">🪙 +{chestReward?.coins}</div>
                  {chestReward?.streak_bonus > 0 && (
                    <div className="font-vt text-sm text-[#0fd6a8] mt-1" data-testid="chest-streak-bonus">🔥 {lang === "en" ? `${chestReward.streak}-day streak · +${chestReward.streak_bonus} bonus` : `Racha de ${chestReward.streak} días · +${chestReward.streak_bonus} de bono`}</div>
                  )}
                  {giftChar && (
                    <div className="mt-3">
                      <div className="font-vt text-sm text-white/70">{lang === "en" ? "New character unlocked!" : "¡Personaje nuevo desbloqueado!"}</div>
                      <div className="font-pixel text-[11px] mt-1" style={{ color: giftChar.colors?.accent || "#8be0ff" }}>{giftChar.name}</div>
                    </div>
                  )}
                  <p className="font-vt text-xs text-white/45 mt-3">{lang === "en" ? "Find one in every level, once a day!" : "¡Encontrá uno en cada nivel, una vez al día!"}</p>
                  <button data-testid="chest-close-btn" onClick={closeChest} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs mt-4">{lang === "en" ? "Awesome!" : "¡Genial!"}</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
